export type PublicSearch = Record<string, unknown>;

export interface PublicRentalApplicationUnitLocationState {
  publicUnitKey?: string;
  propertyName?: string;
  unitName?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  bedrooms?: number;
  bathrooms?: number;
  areaSqft?: number;
  rent?: number;
  availableFrom?: string;
  applyEligible?: boolean;
  organizationName?: string;
  photoUrls?: string[];
  coverImageUrl?: string;
  petFriendly?: boolean;
  leaseTermMonths?: number;
  description?: string;
}

export interface PublicRentalApplicationLocationState {
  publicRentalApplicationUnit?: PublicRentalApplicationUnitLocationState;
}

export function preservePublicSearch(search: PublicSearch): PublicSearch {
  return { ...search };
}

export function renewalTokenFromSearch(search: PublicSearch): string {
  return typeof search.token === 'string' ? search.token.trim() : '';
}

/**
 * Where the pages live on an org host (spec 2.3). The first argument is kept for the callers that pass the business's
 * scope key; the host itself is the scope now, so it does not appear in the path.
 */
export function rentalListingPath(_organizationSlug: string): string {
  return '/';
}

/** Where the Views read their data when they are not seeded: the storefront's own routes. Geocoding is the platform's job. */
export const publicRentalDataPaths = {
  listings: '/homes.json',
  unit: (key: string) => `/homes.json/${encodeURIComponent(key)}`,
  geocode: null as ((address: string) => string) | null,
};

export function rentalUnitPath(
  _organizationSlug: string,
  unitPublicKey: string,
  moveIn?: string | null,
): string {
  const path = `/homes/${encodeURIComponent(unitPublicKey)}`;
  return moveIn ? `${path}?moveIn=${encodeURIComponent(moveIn)}` : path;
}

export function rentalApplicationPath(
  _organizationSlug: string,
  unitPublicKey: string,
): string {
  return `/apply/${encodeURIComponent(unitPublicKey)}`;
}

export function publicRenewalQueryKey(token: string): readonly [
  'public-renewal-response-token',
  string,
] {
  return ['public-renewal-response-token', token];
}

export interface PublicListingQueryFilters {
  beds: string;
  baths: string;
  rentMin: string;
  rentMax: string;
  availableFrom: string;
  petFriendly: string;
  leaseTermMonths: string;
}

export function publicListingQueryKey(
  organizationSlug: string,
  filters: PublicListingQueryFilters,
): readonly [string, string, string, string, string, string, string, string, string] {
  return [
    'public-rental-listing',
    organizationSlug,
    filters.beds,
    filters.baths,
    filters.rentMin,
    filters.rentMax,
    filters.availableFrom,
    filters.petFriendly,
    filters.leaseTermMonths,
  ];
}

export function publicUnitDetailQueryKey(
  organizationSlug: string,
  publicKey: string | null,
): readonly ['public-unit-detail', string, string | null] {
  return ['public-unit-detail', organizationSlug, publicKey];
}

export function publicRentalApplicationUnitQueryKey(
  organizationSlug: string,
  unitPublicKey: string,
): readonly ['public-rental-unit-detail', string, string] {
  return ['public-rental-unit-detail', organizationSlug, unitPublicKey];
}
